import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import CreateServiceProductTemplatePageModal from '@modals/CreateServiceProductTemplatePageModal'
import DuplicateServiceProductModal from '@modals/DuplicateServiceProductModal'
import EditProductTaskSettingsModal from '@modals/EditProductTaskSettingsModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    CreateServiceProductTemplatePageModal: {
      closeModal: () => setState({ showCreateServiceProductTemplatePageModal: false }),
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
    DuplicateServiceProductModal: {
      closeModal: () => setState({ showDuplicateServiceProductModal: false }),
      duplicateServiceProduct: payload => handleCallbackAction(payload),
    },
    EditProductTaskSettingsModal: {
      closeModal: () => setState({ showEditProductTaskSettingsModal: false }),
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateServiceProductTemplatePageModal: false,
  showEditProductTaskSettingsModal: false,
  showDuplicateServiceProductModal: false,
  showUploadEntityAssetsModal: false,
}

const Edit = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateServiceProductTemplatePageModal,
    showDuplicateServiceProductModal,
    showEditProductTaskSettingsModal,
    showUploadEntityAssetsModal,
  } = state

  const { serviceProductId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: payload => handleCallbackAction(payload),
      showCreateServiceProductTemplatePageModal: (payload) => {
        setModalData('CreateServiceProductTemplatePageModal', payload)
        setState({ showCreateServiceProductTemplatePageModal: true })
      },
      showDuplicateServiceProductModal: (payload) => {
        setModalData('DuplicateServiceProductModal', payload)
        setState({ showDuplicateServiceProductModal: true })
      },
      showEditProductTaskSettingsModal: (payload) => {
        setModalData('EditProductTaskSettingsModal', payload)
        setState({ showEditProductTaskSettingsModal: true })
      },
      showUploadEntityAssetsModal: (payload) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent serviceProductId={serviceProductId} />

        <CreateServiceProductTemplatePageModal
          callbacks={callbacks('CreateServiceProductTemplatePageModal', setState)}
          showModal={showCreateServiceProductTemplatePageModal}
        />

        <DuplicateServiceProductModal
          callbacks={callbacks('DuplicateServiceProductModal', setState)}
          showModal={showDuplicateServiceProductModal}
        />

        <EditProductTaskSettingsModal
          callbacks={callbacks('EditProductTaskSettingsModal', setState)}
          showModal={showEditProductTaskSettingsModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Edit
